import React from 'react';

import {
  FrameCorners,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Pause,
  Play,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import CSSModules from 'react-css-modules';
import Cropper from 'react-easy-crop';

import {getSecondsToSMPTE} from '@edume/bento/videoUtil';

import LoadingIndicator from '../LoadingIndicator';
import {CROP_AREA} from './useVideoCropper';
import type {useVideoCropper} from './useVideoCropper';
import {VideoTrimSlider} from './VideoCropperComponents/VideoTrimSlider';

import styles from './styles.module.scss';

const THUMBNAIL_CONTAINER_WIDTH = 702;
const THUMBNAIL_COUNT = 15;
const ZOOM_STEP = 0.1;

type VideoCropperBodyProps = {
  isLoading?: boolean;
  videoCropperProps: Omit<ReturnType<typeof useVideoCropper>, 'getCropResult'>;
};

const VideoCropperBodyComponent = ({
  isLoading,
  videoCropperProps,
}: VideoCropperBodyProps) => {
  const {
    cropperRef,
    videoRef,
    fileUrl,
    thumbnailsLoading,
    videoThumbnails,
    videoDuration,
    videoCurrentTime,
    videoStart,
    videoEnd,
    crop,
    zoom,
    minZoom,
    restrictPosition,
    setVideoStart,
    setVideoEnd,
    setCrop,
    setZoom,
    onMediaLoaded,
    onCropAreaChange,
    onCropComplete,
    initialCroppedAreaPixels,
  } = videoCropperProps;

  return (
    <>
      <div styleName='crop-area'>
        <ul styleName='zoom-actions'>
          <li>
            <button
              onClick={() => setZoom(zoom + ZOOM_STEP)}
              tabIndex={0}
              styleName='action-button'
            >
              <MagnifyingGlassPlus weight='bold' />
            </button>
          </li>
          <li>
            <button
              onClick={() =>
                setZoom(zoom - ZOOM_STEP < 1 ? 1 : zoom - ZOOM_STEP)
              }
              tabIndex={0}
              styleName='action-button'
            >
              <MagnifyingGlassMinus weight='bold' />
            </button>
          </li>
          <li>
            <button
              onClick={() => {
                setZoom(minZoom);
                setCrop({x: 0, y: 0});
              }}
              tabIndex={0}
              styleName='action-button'
            >
              <FrameCorners weight='bold' />
            </button>
          </li>
        </ul>

        <Cropper
          mediaProps={{loop: false, autoPlay: false}}
          ref={cropperRef}
          video={fileUrl}
          crop={crop}
          zoom={zoom}
          minZoom={minZoom}
          aspect={CROP_AREA.width / CROP_AREA.height}
          cropSize={CROP_AREA}
          restrictPosition={restrictPosition}
          initialCroppedAreaPixels={initialCroppedAreaPixels}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onMediaLoaded={onMediaLoaded}
          onCropAreaChange={onCropAreaChange}
          onCropComplete={onCropComplete}
        />

        {isLoading && (
          <div styleName='crop-modal-loading-indicator'>
            <LoadingIndicator
              color='light'
              containerStyle='centerFlex'
              indicator='hexagons'
              size='normal'
            />
          </div>
        )}
      </div>

      <div
        styleName={classNames('timeline', {
          'thumbnails-loading': isLoading || thumbnailsLoading,
        })}
      >
        {videoRef && (
          <div styleName='timeline-play-pause'>
            <button
              onClick={() => {
                if (videoRef.paused) {
                  videoRef.play();
                } else {
                  videoRef.pause();
                }
              }}
              tabIndex={0}
              styleName='play-pause-button'
            >
              {videoRef.paused || isLoading ? (
                <Play size={24} weight='fill' />
              ) : (
                <Pause size={24} weight='fill' />
              )}
            </button>
          </div>
        )}

        <div styleName='timeline-thumbnail'>
          <span
            styleName='video-duration'
            style={{
              left: (videoCurrentTime / videoDuration) * 100 + '%',
            }}
          >
            <i>{getSecondsToSMPTE(videoCurrentTime, 24).slice(3)}</i>
          </span>

          <span
            styleName='left-scene'
            style={{
              width: `${(videoStart / videoDuration) * 100}%`,
            }}
          />
          <span
            styleName='right-scene'
            style={{
              width: `${((videoDuration - videoEnd) / videoDuration) * 100}%`,
            }}
          />

          <div styleName='video-scene'>
            {videoThumbnails.map((item, i) => (
              <img
                key={i}
                src={item}
                height={48}
                width={THUMBNAIL_CONTAINER_WIDTH / THUMBNAIL_COUNT}
                style={{
                  objectFit: 'scale-down',
                  background: 'black',
                }}
              />
            ))}
          </div>
          <VideoTrimSlider
            videoStart={videoStart}
            videoEnd={videoEnd}
            videoDuration={videoDuration}
            setVideoStart={setVideoStart}
            setVideoEnd={setVideoEnd}
          />
        </div>
      </div>
    </>
  );
};

export const VideoCropperBody = CSSModules(VideoCropperBodyComponent, styles, {
  allowMultiple: true,
});
